import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-cream" data-id="element-169" data-mp="2zssblzvnhgae">
      <div className="max-w-5xl mx-auto" data-id="element-170" data-mp="3dqpcv9l1uave">
        <div className="text-center mb-10" data-id="element-171" data-mp="3dpnwcpl9kw6a">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 text-navy" data-id="element-172" data-mp="38qulz619xlac">
            See Inside Taekwondo Instructor Ready
          </h2>
          <p className="text-lg text-ink max-w-3xl mx-auto" data-id="element-173" data-mp="2l21j0pm8id68">
            Real pages from the system — visual progressions, ready-to-use drills, complete class
            structures and professional coaching tools.
          </p>
        </div>

        <div className="relative" data-id="element-174" data-mp="2q14o2a27sje7">
          <div className="relative bg-cream-card border border-gold-light rounded-2xl shadow-sm overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-175" data-mp="2q0341g380mkc">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-176" data-mp="19ca6vimd7p1">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}: ${slide.title}`} data-id="element-177" data-mp="16yeckg47rgf">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none" data-id="element-178" data-mp="17r7rqw2e9gqm" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-navy/90 text-gold flex items-center justify-center hover:bg-navy transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" aria-label="View image larger" data-id="element-179" data-mp="2jd0vh98hsmqr">
              <Maximize2 size={18} data-id="element-180" data-mp="20i3vyashjtp7" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-navy text-gold border border-gold/50 shadow-lg flex items-center justify-center hover:bg-navy-deep transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" aria-label="Previous image" data-id="element-181" data-mp="1vihetq9jdtb4">
            <ChevronLeft size={24} data-id="element-182" data-mp="2djltk6yewgnl" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-navy text-gold border border-gold/50 shadow-lg flex items-center justify-center hover:bg-navy-deep transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" aria-label="Next image" data-id="element-183" data-mp="2dj2c0rcobua3">
            <ChevronRight size={24} data-id="element-184" data-mp="1yu63yuamah9h" />
          </button>
        </div>

        <p className="text-center text-navy font-bold text-sm sm:text-base mt-6" data-id="element-185" data-mp="1ys36jp74xluq">{slide.title}</p>

        <div className="flex justify-center items-center gap-3 mt-4" data-id="element-186" data-mp="1kx0s2do82o6b">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold ${dotIndex === index ? 'w-8 bg-gold' : 'w-2.5 bg-navy/25 hover:bg-navy/40'}`} aria-label={`Go to image ${dotIndex + 1}: ${item.title}`} aria-current={dotIndex === index} data-id="element-187" data-mp="167pmkgmqa3jt" data-mpi={dotIndex} />)}
        </div>
      </div>

      <AnimatePresence data-id="element-188" data-mp="1k5wcnalfqz8x">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-navy-deep/95 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.title} onClick={() => setIsZoomed(false)} data-id="element-189" data-mp="1yvlivk8dxtg2">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" aria-label="Close enlarged image" data-id="element-190" data-mp="2ax3aqilxgi64">
              <X size={22} data-id="element-191" data-mp="2yo8p0tc7z2eb" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-192" data-mp="2ymxkusev3smo" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}